import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, LockKeyhole, Store as StoreIcon } from "lucide-react";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { storeAdminLogin, isApiSuccess } from "../../../services/api";

export default function StoreLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useDocumentTitle("Store Login");

  useEffect(() => {
    try {
      const auth = JSON.parse(
        localStorage.getItem("store_panel_auth") || "null"
      );

      if (auth?.token) {
        navigate("/store-panel/", { replace: true });
      }
    } catch {
      localStorage.removeItem("store_panel_auth");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let admin = 0
      // The API must validate BOTH email and password.
      const res = await storeAdminLogin(email.trim(),admin, password);

      if (!isApiSuccess(res) || !res?.access_token) {
        throw new Error(
          res?.message || "Invalid email or password."
        );
      }

      localStorage.setItem(
        "store_panel_auth",
        JSON.stringify({
          token: res.access_token,
          tokenType: res.token_type,
          store: res.store,
          loggedInAt: Date.now(),
        })
      );

      const requestedPath = location.state?.from?.pathname;

      // Only allow redirects within the store panel.
      const targetPath =
        requestedPath?.startsWith("/store-panel")
          ? requestedPath
          : "/store-panel/";

      navigate(targetPath, { replace: true });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Store login failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-3">
      <div className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-lg">
        <div className="bg-orange-50 p-4 text-center">
          <img
            src="/logo.png"
            alt="Goerone"
            className="mx-auto h-20"
          />

          <div className="mt-1.5 flex items-center justify-center gap-1.5">
            <StoreIcon size={18} className="text-orange-500" />
            <p className="font-semibold text-orange-500">
              Store Panel
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <h2 className="text-xl font-semibold">
            Store Login
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Sign in using your registered store credentials.
          </p>

          {error && (
            <div
              role="alert"
              className="mt-2 rounded-lg bg-red-50 p-2 text-sm text-red-600"
            >
              {error}
            </div>
          )}

          <label className="mt-3 block text-sm font-medium">
            Email address
          </label>

          <div className="mt-2 flex items-center overflow-hidden rounded-lg border focus-within:border-orange-500">
            <Mail size={19} className="ml-3 text-gray-500" />
            <input
              type="email"
              placeholder="Enter store email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              className="w-full p-3 outline-none"
            />
          </div>

          <label className="mt-3 block text-sm font-medium">
            Password
          </label>

          <div className="mt-2 flex items-center overflow-hidden rounded-lg border focus-within:border-orange-500">
            <LockKeyhole
              size={19}
              className="ml-3 text-gray-500"
            />
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="w-full p-3 outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-3 w-full rounded-lg bg-black px-4 py-3 font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Logging in..." : "Login to Store"}
          </button>
        </form>
      </div>
    </div>
  );
}